import { useCallback, useEffect, useRef, useState } from 'react'

import { useNavigate } from 'react-router-dom'
import i18n from 'src/i18n'

import { consumePostLoginRedirect } from 'src/app/auth/adapters/oauth-redirect/post-login-redirect-store'
import {
  usePolicyAcceptanceMutation,
  usePolicyAcceptanceQuery,
} from 'src/app/auth/queries/policy-acceptance'
import { useSignoutMutation } from 'src/app/auth/queries/signout'
import { Toast, toastTypes } from 'src/app/core/services/toast'

export interface IUseTermsOfServiceReturn {
  sentinelRef: React.RefObject<HTMLDivElement>
  hasScrolledToBottom: boolean
  handleAccept: () => Promise<void>
  handleDecline: () => Promise<void>
  isSigningOut: boolean
  isAccepting: boolean
}

export const useTermsOfService = (): IUseTermsOfServiceReturn => {
  const navigate = useNavigate()
  const sentinelRef = useRef<HTMLDivElement>(null)
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false)
  const { mutateAsync: signout, isPending: isSigningOut } = useSignoutMutation()
  const { mutateAsync: acceptPolicy, isPending: isAccepting } =
    usePolicyAcceptanceMutation()

  const policyQuery = usePolicyAcceptanceQuery({ enabled: true })

  useEffect(() => {
    if (!policyQuery.isSuccess) return
    if (policyQuery.data.accepted) {
      const redirectTo = consumePostLoginRedirect() ?? '/'
      navigate(redirectTo, { replace: true })
    }
  }, [policyQuery.isSuccess, policyQuery.data, navigate])

  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setHasScrolledToBottom(true)
          observer.disconnect()
        }
      },
      { threshold: 0.1 },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [])

  const handleDecline = useCallback(async (): Promise<void> => {
    await signout()
    navigate('/')
  }, [navigate, signout])

  const handleAccept = useCallback(async (): Promise<void> => {
    try {
      await acceptPolicy()
      const redirectTo = consumePostLoginRedirect() ?? '/'
      navigate(redirectTo, { replace: true })
    } catch {
      Toast.notify({
        title: i18n.t('auth.termsPage.errors.acceptFailedTitle'),
        message: i18n.t('auth.termsPage.errors.acceptFailed'),
        type: toastTypes.ERROR,
      })
      await handleDecline()
    }
  }, [acceptPolicy, navigate, handleDecline])

  return {
    sentinelRef,
    hasScrolledToBottom,
    handleAccept,
    handleDecline,
    isSigningOut,
    isAccepting,
  }
}
